/**
 * operationTypeVocabulary — the one Hebrew vocabulary for a tool's OPERATION CLASS.
 *
 * This is the five-arm `OperationType` enum declared in `backend/app/services/tool_registry.py`
 * (`class OperationType(str, Enum)`): `read_only`, `write`, `execute`, `delete`, `admin`. The
 * registry's own header calls it "the single source of truth for per-tool permission metadata",
 * and it is not a label an operator may take or leave: `autonomy_policy.check_action` reads
 * `operation_type.value == "read_only"` to decide that a tool needs no approval at any autonomy
 * level, so this class is the line between a read-only probe and a state-changing call.
 *
 * All five arms are live. The registry mints every one of them — `read_only` on the analysis and
 * lookup tools, `write` on the comment / ticket-state / mail tools, `execute` on the
 * restart / install / script tools, `delete` on the account-and-device removal tools and `admin`
 * on the firewall and admin-account tools — so a map holding only `read_only` and `write` would be
 * short by three arms the producer can send.
 *
 * `operation_type` is serialized to the frontend by seven producers (`routers/connections.py`,
 * `routers/agents.py` twice, `services/employee_workspace.py`, `services/specialist_workspace.py`,
 * `services/remote_operations.py`, `services/remote_readiness.py`), and the reachable arm set is
 * per-endpoint rather than global:
 *
 *   - `GET /api/connections/health` (`routers/connections.py`) serves exactly two tools,
 *     `check_executable` and `endpoint_diagnosis`, and both are `READ_ONLY` in the registry — so
 *     the Connections table can show `read_only` and nothing else today.
 *   - `GET /api/agents/{id}/workspace` carries `capabilities.tools` straight out of
 *     `employee_workspace.entitled_tools_for`, which walks the **whole** registry and filters by
 *     entitlement — so every one of the five arms reaches the Remote Operations page for an
 *     employee entitled to it.
 *
 * The file exists because those two surfaces did not treat the scale as one. `RemoteOperationsPage`
 * kept a private copy of it and ran both of its sites through `MAP[value] ?? value`, so a tool
 * carrying a class the copy lacks reached the operator as the producer's own identifier; and
 * `ConnectionsPage` printed `{tool.operation_type}` raw under a Hebrew `פעולה` column header, one
 * cell away from the Risk column this same row words in Hebrew.
 *
 * Two rules this module exists to hold, the same two `runStatusVocabulary` and
 * `riskGradeVocabulary` hold:
 *
 *   1. One scale, re-derived from the producer. `operationTypeVocabulary.test.js` parses the
 *      `OperationType` arms out of the Python source and asserts this map's key set EQUALS them,
 *      and re-derives the registry's own per-tool assignments a second time, so a sixth arm or a
 *      renamed one reddens the suite instead of shipping an English token to an operator.
 *   2. An unrecognised value is NAMED, never echoed — the reason `operationTypeLabel` will not put
 *      a raw token on screen. A value outside the enum means this bundle is behind the producer.
 *
 * Wording. The words are the ones `RemoteOperationsPage`'s deleted page-local map already used
 * (`קריאה בלבד` / `כתיבה` / `הרצה` / `מחיקה` / `ניהול`), so only the two frames change. They are
 * bare nouns, because both call sites supply their own frame: the approval list prints the word
 * after a tool name, and the "allowed operation types" row prints it as a `word: count` pair.
 *
 * One named consequence of rule 2 on that row: its keys come from `t.operation_type ?? "unknown"`,
 * so if this bundle is behind the producer and two tools carry two different unrecognised classes,
 * the row prints `סוג פעולה לא מזוהה: 1` and `סוג פעולה לא מזוהה: 2` — two identical labels. That
 * is deliberate. The alternative is echoing the producer's token into a Hebrew row, which is the
 * defect this file exists to remove, and the row's counts still reconcile.
 *
 * WHAT THIS FILE CANNOT SEE, stated so the coverage claim cannot go short. `riskGradeVocabulary`
 * words `Tool.risk_level`, which is a different field on the same Connections row and is already
 * consolidated; `toolNameVocabulary` words tool *names*, which is a third field. The five
 * `operation_type` comparisons in `EmployeeBuilderPage`, `AcronisSpecialistPage`,
 * `EsetSpecialistPage`, `M365SpecialistPage` and `SpecialistWorkspacePage` are predicates that
 * partition a tool list and render nothing, so they are deliberately not call sites; if one of
 * them ever starts printing the token it becomes one.
 */

/**
 * The five arms of the Tool Registry's `OperationType`, by wire value.
 *
 * Keys are the exact strings `OperationType.value` carries — `str`-valued enum members that
 * serialize as themselves.
 */
export const OPERATION_TYPE_HE = {
  read_only: "קריאה בלבד",
  write: "כתיבה",
  execute: "הרצה",
  delete: "מחיקה",
  admin: "ניהול",
};

/** Shown for a value the enum does not hold. Never the raw token itself. */
export const OPERATION_TYPE_UNKNOWN_HE = "סוג פעולה לא מזוהה";

/**
 * The Hebrew word for a tool's operation class. `hasOwnProperty` rather than
 * `OPERATION_TYPE_HE[value] ||`: a bare lookup answers an inherited key (`"constructor"`,
 * `"toString"`) with a function, which a `||` fallback would treat as a hit and render.
 *
 * `null`, `undefined` and a `"unknown"` bucket key all land on the named unknown rather than on an
 * empty string or on the token a caller happened to hold, so no site can print a class it does not
 * have.
 */
export function operationTypeLabel(value) {
  return Object.prototype.hasOwnProperty.call(OPERATION_TYPE_HE, value)
    ? OPERATION_TYPE_HE[value]
    : OPERATION_TYPE_UNKNOWN_HE;
}
